import React, { useCallback, useEffect, useState } from "react";
import type { GetStaticProps } from "next";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { Button } from "@/components/ui/button";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "next-i18next";
import clsx from "clsx";
import { Table, toaster } from "evergreen-ui";
import Upload from "@/components/Upload";
import AdBanner from "@/components/AdBanner";
import UploadLoader from "@/components/Upload/Loader";
import { ArrowDownToLine, Loader2, Settings2, Trash2 } from "lucide-react";
import { IFile, IFileStatus, ProcessFileQueue } from "@/utils/engines/queue";
import { getWorker } from "@/utils/workerWrapper";
import { CompressAlertDialog } from "@/components/CompressDialog";
import { Mimes } from "@/utils/engines/mimes";
import { Dimension, ProcessOutput } from "@/utils/engines/ImageBase";
import { DefaultCompressOption } from "@/stores/compress";
import { AvifImage } from "@/utils/engines/AvifImage";
import { CompressFormOption } from "@/components/CompressOption";
import { avifCheck } from "@/utils/engines/support";
import {
  formatSize,
  getCompressRatio,
  getOutputFileName
} from "@/utils/engines/utils";
import CompressPreviewCard from "@/components/CompressPreviewCard";

type Props = {
  // Add custom props here
};

function convertMBtoBytes(mb: number) {
  return mb * Math.pow(1024, 2);
}

function generateRandomString(length: number) {
  var result = "";
  var characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

const MAX_FILES = 30;
const MAX_SIZE = 15;
const Folder = "Douni.one";
let compressWorker;

interface OutputMessage {
  width: number;
  height: number;
  id: string;
  compress?: ProcessOutput;
  preview?: ProcessOutput;
}

/**
 * Create a download dialog from browser
 * @param name
 * @param blob
 */
export function createDownload(name: string, blob: Blob) {
  const anchor = document.createElement("a");
  anchor.href = URL.createObjectURL(blob);
  anchor.download = name;
  anchor.click();
  anchor.remove();
}
function DownloadAction({
  size,
  className
}: {
  size: number;
  className?: string;
}) {
  return <ArrowDownToLine size={size} className={className} />;
}
export default function CompressImagePage() {
  const { t } = useTranslation("compress-image");
  const [uploadLoading, setUploadLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [content, setContent] = useState<string>("");
  const [downloadActionShow, setDownloadActionShow] = useState(false);
  const queue = new ProcessFileQueue(3);
  const [files, setFiles] = useState<IFile[]>([]);
  const [option, setOption] = useState(DefaultCompressOption);
  const [settingSheetOpen, setSettingSheetOpen] = useState(false);
  const [preview, setPreview] = useState<IFile | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const transformOutput = async (item: IFile, compressFile: OutputMessage) => {
    const info: IFile = {
      ...item,
      width: compressFile.width,
      height: compressFile.height,
      status: IFileStatus.Compressed,
      compressFileSize: compressFile.compress?.blob?.size,
      ...compressFile
    };
    if (
      item.blob.type === Mimes.svg &&
      compressFile.compress &&
      option.format.target
    ) {
      const target = option.format.target;

      info.compress = compressFile.compress;
      const canvas = document.createElement("canvas");
      canvas.width = info.width;
      canvas.height = info.height;
      const context = canvas.getContext("2d")!;
      if (["jpg", "jpeg"].includes(target)) {
        context.fillStyle = option.format.transparentFill;
        context.fillRect(0, 0, info.width, info.height);
      }
      const svg = await new Promise<HTMLImageElement>(resolve => {
        const img = new Image();
        img.src = info.compress!.src;
        img.onload = () => resolve(img);
      });
      context.drawImage(
        svg,
        0,
        0,
        info.width,
        info.height,
        0,
        0,
        info.width,
        info.height
      );

      // Convert svg to target type
      let blob: Blob;
      if (target === "avif") {
        blob = await AvifImage.encode(
          context,
          info.width,
          info.height,
          option.avif.quality,
          option.avif.speed
        );
      } else {
        blob = await new Promise<Blob>(resolve => {
          canvas.toBlob(
            result => {
              resolve(result!);
            },
            Mimes[target],
            1
          );
        });
      }
      info.compress!.blob = blob;
      info.compress!.src = URL.createObjectURL(blob);
    }
    return info;
  };

  useEffect(() => {
    avifCheck();
    compressWorker =
      compressWorker ||
      getWorker(
        new Worker(new URL("@workers/compress.worker", import.meta.url))
      );
  }, []);

  const addTask = (file: IFile) => {
    console.log("option-->", option);
    compressWorker =
      compressWorker ||
      getWorker(
        new Worker(new URL("@workers/compress.worker", import.meta.url))
      );
    const resolve = async (compressFile: OutputMessage) => {
      const info = await transformOutput(file, compressFile);
      console.log("info-->", info);
      setFiles(prevFiles =>
        prevFiles.map(item => {
          if (item.id === file.id) {
            return info;
          }
          return item;
        })
      );
    };
    const reject = (error: any) => {
      console.log("reject-->", error);
      setFiles(prevFiles =>
        prevFiles.map(item => {
          if (item.id === file.id) {
            return { ...item, status: IFileStatus.CompressError };
          }
          return item;
        })
      );
    };
    const active = () => {
      setFiles(prevFiles =>
        prevFiles.map(item => {
          if (item.id === file.id) {
            return { ...item, status: IFileStatus.Compressing };
          }
          return item;
        })
      );
    };
    queue.register({
      id: file.id,
      type: "compress",
      worker: compressWorker,
      payload: {
        info: file,
        option: option
      },
      active,
      reject,
      resolve
    });
    // return queue.addTask(file, compressWorker, active, resolve, reject);
  };

  const downloadAll = async () => {
    const zip = new JSZip();
    const promiseTask = [];
    setDownloadActionShow(true);
    setDownloadLoading(true);
    try {
      const _folder = zip.folder(Folder);
      files.forEach(file => {
        if (file.status === IFileStatus.Compressed) {
          const filename = getOutputFileName(file, option);
          _folder.file(filename, file.compress?.blob);
        }
      });

      const content = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: {
          level: 6
        }
      });
      const fileName = `Douni.one.zip`;
      saveAs(content, fileName);
      setContent(
        t("tooltip.download-success")?.replace(
          "{count}",
          files.filter(file => file.status === IFileStatus.Compressed).length +
            ""
        )
      );
    } catch (error) {
      setContent(t("tooltip.download-error"));
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <Card className={clsx("w-full mt-8 md:mt-0")}>
      <CardHeader>
        <CardTitle>{t("title")}</CardTitle>
        <CardDescription>{t("subtitle")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <Upload
          className="w-full"
          loading={uploadLoading}
          placeholder={t("input.title")}
          placeholderText={t("input.placeholder")
            ?.replace("{maxFiles}", `${MAX_FILES}`)
            ?.replace("{maxSize}", `${MAX_SIZE}`)}
          onDropRejected={() => {
            toaster.danger(t("tooltip.reject"));
          }}
          accept={{
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "image/webp": [".webp"],
            "image/gif": [".gif"],
            "image/svg+xml": [".svg"],
            "image/avif": [".avif"]
          }}
          maxFiles={MAX_FILES}
          maxSize={convertMBtoBytes(MAX_SIZE)}
          onFileChange={async _files => {
            const iFile = _files.map(file => {
              return {
                id: generateRandomString(12),
                originalFile: file,
                name: file.name,
                blob: file,
                src: URL.createObjectURL(file),
                fileSize: file.size,
                width: 0,
                height: 0,
                status: IFileStatus.Waiting,
                createAt: Date.now()
              };
            });
            setFiles(
              [...files, ...iFile].sort((a, b) => b.createAt - a.createAt)
            );
          }}
        />
        <div className="action mt-5 flex flex-col md:flex-row gap-4 justify-between">
          <div className="w-full flex flex-row gap-4">
            <Button
              className="w-[180px]"
              size="default"
              disabled={
                files.filter(file => file.status === IFileStatus.Waiting)
                  .length <= 0
              }
              onClick={async () => {
                files.forEach(async file => {
                  if (file.status === IFileStatus.Waiting) {
                    if (file.originalFile.type === Mimes.svg) {
                      const { width, height } = await new Promise<Dimension>(
                        resolve => {
                          const img = new Image();
                          img.src = file.src;
                          img.onload = () => {
                            resolve({
                              width: img.width,
                              height: img.height
                            });
                          };
                        }
                      );
                      file.width = width;
                      file.height = height;
                    }
                    addTask(file);
                  }
                });
                await queue.scheduler();
              }}
            >
              {t("action.compress")}
            </Button>

            {files.length > 0 && (
              <Button
                variant="outline"
                className="w-[180px]"
                size="default"
                onClick={() => {
                  setFiles([]);
                }}
              >
                {t("action.clear")}
              </Button>
            )}
          </div>
          <div className="flex flex-row gap-4">
            <Button
              className="w-[200px]"
              size="default"
              variant="outline"
              onClick={() => setSettingSheetOpen(true)}
            >
              <Settings2 className="h-4 w-4 mr-2" />
              {t("action.setting")}
            </Button>
            {files.filter(file => file.status === IFileStatus.Compressed)
              .length > 1 && (
              <Button
                variant="default"
                className="w-[180px]"
                size="default"
                onClick={downloadAll}
              >
                {t("action.download-all")}
              </Button>
            )}
          </div>
        </div>

        {files.length > 0 && (
          <Table className="my-5 dark:bg-slate-900">
            {/* <Table.Head>
              <Table.TextHeaderCell>
                {t("table.head.name")}
              </Table.TextHeaderCell>
              <Table.TextHeaderCell>
                {t("table.head.size")}
              </Table.TextHeaderCell>
              <Table.TextHeaderCell />
              <Table.TextHeaderCell>
                {t("table.head.status")}
              </Table.TextHeaderCell>
            </Table.Head> */}
            <Table.Body height={240}>
              {files.map(file => (
                <Table.Row
                  className="dark:!bg-slate-900 dark:!text-slate-300"
                  key={file.id}
                >
                  <Table.TextCell className="dark:!text-slate-300">
                    {file.name}
                  </Table.TextCell>
                  <Table.TextCell className="dark:!text-slate-300">
                    <div className="relative w-12 h-12 flex items-center justify-center overflow-hidden rounded-xl group">
                      {file.status === IFileStatus.Compressed ? (
                        <img
                          className="w-full h-full object-cover"
                          src={file.preview.src}
                        />
                      ) : file.src ? (
                        <img
                          className="w-full h-full object-cover"
                          src={file.src}
                        />
                      ) : (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      {file.compress && (
                        <div
                          className="flex items-center justify-center cursor-pointer absolute top-0 left-0 w-full h-full bg-black bg-opacity-60 rounded-md text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                          onClick={async () => {
                            // gstate.loading = true;
                            // await wait(300);
                            setPreview(file);
                            setPreviewVisible(true);
                          }}
                        >
                          <svg
                            className="w-4 h-4 fill-current"
                            viewBox="0 0 24 24"
                          >
                            <path d="M13,23H11V1H13V23M9,19H5V5H9V3H5C3.89,3 3,3.89 3,5V19C3,20.11 3.9,21 5,21H9V19M19,7V9H21V7H19M19,5H21C21,3.89 20.1,3 19,3V5M21,15H19V17H21V15M19,11V13H21V11H19M17,3H15V5H17V3M19,21C20.11,21 21,20.11 21,19H19V21M17,19H15V21H17V19Z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </Table.TextCell>
                  <Table.TextCell isNumber className="dark:!text-slate-300">
                    {formatSize(file.fileSize)}
                    {file.status === IFileStatus.Compressed && (
                      <>
                        <span>/{formatSize(file.compressFileSize)}</span>
                        <span className="pl-2">
                          -
                          {getCompressRatio(
                            file.fileSize,
                            file.compressFileSize
                          )}
                          %
                        </span>
                      </>
                    )}
                  </Table.TextCell>

                  {[IFileStatus.Compressing, IFileStatus.Waiting].includes(
                    file.status
                  ) && (
                    <Table.TextCell>
                      <UploadLoader
                        // animation
                        animation={file.status === IFileStatus.Compressing}
                      />
                    </Table.TextCell>
                  )}
                  <Table.TextCell className="dark:!text-slate-300">
                    {t(`status.${file.status}`)}
                  </Table.TextCell>
                  {file.status === IFileStatus.Compressed && (
                    <>
                      <Table.TextCell>
                        <div className="flex flex-row gap-2">
                          <div
                            className="w-8 cursor-pointer dark:!text-slate-300"
                            onClick={() => {
                              setFiles(
                                files.filter(item => item.id !== file.id)
                              );
                            }}
                          >
                            <Trash2 size={20} />
                          </div>
                          <div
                            className="w-8 cursor-pointer dark:!text-slate-300"
                            onClick={() => {
                              const fileName = getOutputFileName(file, option);

                              saveAs(file.compress?.blob, fileName);
                            }}
                          >
                            <DownloadAction size={20} />
                          </div>
                        </div>
                      </Table.TextCell>
                    </>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </CardContent>
      <CardFooter className="flex-col space-y-4">
        <AdBanner
          className="w-full"
          data-ad-slot="2503417325"
          data-ad-format="autorelaxed"
          data-id="789"
          data-full-width-responsive="true"
        />
      </CardFooter>
      <CompressAlertDialog
        open={downloadActionShow}
        loading={downloadLoading}
        content={content}
        onClose={() => setDownloadActionShow(false)}
      />
      <CompressFormOption
        open={settingSheetOpen}
        setOptions={setOption}
        onClose={() => setSettingSheetOpen(false)}
      />
      {preview && (
        <CompressPreviewCard
          preview={preview}
          onClose={() => {
            setPreviewVisible(false);
            setPreview(null);
          }}
        />
      )}
    </Card>
  );
}

export const getStaticProps: GetStaticProps<Props> = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? "en", [
      "common",
      "description",
      "title",
      "keywords",
      "compress-image"
    ]))
  }
});
